// app/javascript/controllers/slots_controller.js
import { Controller } from "@hotwired/stimulus"

// Stimulus controller pour générer automatiquement les créneaux (matin, après-midi ou les deux)
// Filtre automatiquement les week-ends et jours fériés.
// Après génération, vérifie les conflits formateur via API et affiche des warnings visuels.
export default class extends Controller {
  static targets = ["startDate", "endDate", "period", "list", "morningStart", "morningEnd", "afternoonStart", "afternoonEnd", "trainerId", "place", "moduleTitle", "conflictWarning"]

  static values = {
    index: Number,
    holidays: { type: Array, default: [] },
    conflictsUrl: { type: String, default: "/api/session_slots/trainer_conflicts" },
    // Textes UI i18n (audit 2026-08-04, A6-F6) : defaults FR, alimentés par
    // data-slots-*-value avec t() — clés stimulus.slots.*
    missingFieldsMessage: { type: String, default: "Veuillez renseigner une date de début, une date de fin et un lieu." },
    invalidDatesMessage: { type: String, default: "Les dates saisies ne sont pas valides." },
    dateOrderMessage: { type: String, default: "La date de début doit être antérieure à la date de fin." }
  }

  connect() {
    this.updateDuration()
  }

  calculateDuration() {
    const rows = this.listTarget.querySelectorAll('tr')
    let totalDuration = 0

    rows.forEach(row => {
      if (row.classList.contains("hidden")) return
      const startTime = row.querySelector('td:nth-child(2)').textContent
      const endTime = row.querySelector('td:nth-child(3)').textContent

      const start = new Date(`2000-01-01T${startTime}`)
      const end = new Date(`2000-01-01T${endTime}`)

      const duration = (end - start) / (1000 * 60 * 60)
      totalDuration += duration
    })

    return totalDuration.toFixed(1)
  }

  updateDuration() {
    const duration = this.calculateDuration()
    const title = this.moduleTitleTarget.textContent.trim()
    const visibleTitle = this.element.closest('.border').querySelector('.bg-gray-100')
    if (visibleTitle) {
      visibleTitle.textContent = `${title} (${duration}h)`
    }
  }

  getTrainerName() {
    const select = this.trainerIdTarget
    const selectedOption = select.options[select.selectedIndex]
    return selectedOption.text
  }

  async generate() {
    const start = this.startDateTarget.value
    const end = this.endDateTarget.value
    const period = this.periodTarget.value
    const morningStart = this.morningStartTarget.value
    const morningEnd = this.morningEndTarget.value
    const afternoonStart = this.afternoonStartTarget.value
    const afternoonEnd = this.afternoonEndTarget.value
    const trainerId = this.trainerIdTarget.value
    const place = this.placeTarget.value

    if (!start || !end || !place) {
      alert(this.missingFieldsMessageValue)
      return
    }

    const startDate = new Date(start)
    const endDate = new Date(end)

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      alert(this.invalidDatesMessageValue)
      return
    }

    if (startDate > endDate) {
      alert(this.dateOrderMessageValue)
      return
    }

    const dates = this._eachDayBetween(startDate, endDate)

    dates.forEach(date => {
      if (period === "morning" || period === "both") {
        this._appendSlotRow(date, "am", morningStart, morningEnd, trainerId, place)
      }
      if (period === "afternoon" || period === "both") {
        this._appendSlotRow(date, "pm", afternoonStart, afternoonEnd, trainerId, place)
      }
    })

    this.updateDuration()
    this._clearConflictWarnings()
    await this._checkConflicts(trainerId, start, end)
  }

  _appendSlotRow(date, period, startTime, endTime, trainerId, place) {
    const row = document.createElement('tr')
    const trainerName = this.getTrainerName()
    const id = `${Date.now()}${Math.floor(Math.random() * 10000)}`

    const startDateTime = new Date(date)
    startDateTime.setHours(Number(startTime.split(":")[0]), Number(startTime.split(":")[1]), 0, 0)
    const endDateTime = new Date(date)
    endDateTime.setHours(Number(endTime.split(":")[0]), Number(endTime.split(":")[1]), 0, 0)

    row.dataset.slotStart = startDateTime.toISOString()
    row.dataset.slotEnd = endDateTime.toISOString()

    const prefix = `promotion[module_promotions_attributes][${this.indexValue}][session_slots_attributes][${id}]`

    const dateCell = document.createElement("td")
    dateCell.className = "px-3 py-2"
    dateCell.appendChild(document.createTextNode(date.toLocaleDateString("fr-FR")))
    this._addHiddenInput(dateCell, `${prefix}[start_date]`, startDateTime.toISOString())
    this._addHiddenInput(dateCell, `${prefix}[end_date]`, endDateTime.toISOString())
    this._addHiddenInput(dateCell, `${prefix}[place]`, place)
    this._addHiddenInput(dateCell, `${prefix}[trainer_id]`, trainerId)
    this._addHiddenInput(dateCell, `${prefix}[period]`, period)
    this._addHiddenInput(dateCell, `${prefix}[_destroy]`, "false")
    row.appendChild(dateCell)

    row.appendChild(this._textCell(startTime))
    row.appendChild(this._textCell(endTime))
    row.appendChild(this._textCell(place))
    row.appendChild(this._textCell(trainerName))

    const actionCell = document.createElement("td")
    actionCell.className = "px-3 py-2"
    const deleteBtn = document.createElement("button")
    deleteBtn.type = "button"
    deleteBtn.className = "text-red-600 hover:text-red-800"
    deleteBtn.dataset.action = "slots#remove"
    const icon = document.createElement("i")
    icon.className = "fa-light fa-trash-can"
    deleteBtn.appendChild(icon)
    actionCell.appendChild(deleteBtn)
    row.appendChild(actionCell)

    this.listTarget.appendChild(row)
  }

  // Verifie les conflits (formateur + jours feries) via l'API
  async _checkConflicts(trainerId, startDate, endDate) {
    try {
      const params = new URLSearchParams({
        trainer_id: trainerId,
        start_date: startDate,
        end_date: endDate
      })

      const response = await fetch(`${this.conflictsUrlValue}?${params}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content
        }
      })

      if (!response.ok) return

      const data = await response.json()

      if (data.holidays && data.holidays.length > 0) {
        this._markHolidayRows(data.holidays)
      }

      if (data.sessions && data.sessions.length > 0) {
        this._markTrainerConflictingRows(data.sessions)
      }

      this._updateWarningBanner()
    } catch (error) {
      console.warn("Impossible de vérifier les conflits:", error)
    }
  }

  // Marque les lignes qui tombent sur un jour ferie
  _markHolidayRows(holidays) {
    const holidaySet = new Set(holidays)
    const rows = this.listTarget.querySelectorAll("tr")

    rows.forEach(row => {
      if (row.classList.contains("hidden")) return
      const slotStart = row.dataset.slotStart
      if (!slotStart) return

      const date = new Date(slotStart)
      if (holidaySet.has(this._toISODate(date))) {
        this._addWarningToRow(row, "Jour férié : ce créneau tombe sur un jour férié")
      }
    })
  }

  // Compare chaque ligne avec les sessions existantes du formateur
  _markTrainerConflictingRows(existingSessions) {
    const rows = this.listTarget.querySelectorAll("tr")

    rows.forEach(row => {
      if (row.classList.contains("hidden")) return
      const slotStart = row.dataset.slotStart
      const slotEnd = row.dataset.slotEnd
      if (!slotStart || !slotEnd) return

      const rowStart = new Date(slotStart)
      const rowEnd = new Date(slotEnd)

      const hasConflict = existingSessions.some(session => {
        const sStart = new Date(session.start_date)
        const sEnd = new Date(session.end_date)
        return rowStart < sEnd && rowEnd > sStart
      })

      if (hasConflict) {
        this._addWarningToRow(row, "Conflit : le formateur a déjà une session à ce créneau")
      }
    })
  }

  // Ajoute le style warning + icone a une ligne
  _addWarningToRow(row, tooltip) {
    row.classList.add("bg-amber-50")
    const firstCell = row.querySelector("td")
    if (firstCell && !firstCell.querySelector(".conflict-icon")) {
      const warningSpan = document.createElement("span")
      warningSpan.className = "conflict-icon inline-flex items-center ml-1 text-amber-600"
      warningSpan.title = tooltip
      const warningIcon = document.createElement("i")
      warningIcon.className = "fa-light fa-triangle-exclamation"
      warningSpan.appendChild(warningIcon)
      firstCell.appendChild(warningSpan)
    }
  }

  // Met a jour le bandeau d'alerte avec le compte total de conflits
  _updateWarningBanner() {
    if (!this.hasConflictWarningTarget) return

    const count = this.listTarget.querySelectorAll("tr.bg-amber-50:not(.hidden)").length

    if (count === 0) {
      this.conflictWarningTarget.classList.add("hidden")
      this.conflictWarningTarget.textContent = ""
      return
    }

    const plural = count > 1 ? "s" : ""
    this.conflictWarningTarget.textContent = ""

    const icon = document.createElement("i")
    icon.className = "fa-light fa-triangle-exclamation mr-2"
    this.conflictWarningTarget.appendChild(icon)

    const text = document.createTextNode(
      `${count} créneau${plural} en conflit (jour férié ou formateur déjà occupé). Ces créneaux seront rejetés à l'enregistrement.`
    )
    this.conflictWarningTarget.appendChild(text)
    this.conflictWarningTarget.classList.remove("hidden")
  }

  _clearConflictWarnings() {
    this.listTarget.querySelectorAll("tr.bg-amber-50").forEach(row => {
      row.classList.remove("bg-amber-50")
      row.querySelectorAll(".conflict-icon").forEach(el => el.remove())
    })

    if (this.hasConflictWarningTarget) {
      this.conflictWarningTarget.classList.add("hidden")
      this.conflictWarningTarget.textContent = ""
    }
  }

  _eachDayBetween(startDate, endDate) {
    const dates = []
    const current = new Date(startDate)
    const holidaySet = new Set(this.holidaysValue)

    while (current <= endDate) {
      const isWeekend = current.getDay() === 0 || current.getDay() === 6
      const isHoliday = holidaySet.has(this._toISODate(current))

      if (!isWeekend && !isHoliday) {
        dates.push(new Date(current))
      }
      current.setDate(current.getDate() + 1)
    }

    return dates
  }

  _toISODate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  _addHiddenInput(parent, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    parent.appendChild(input)
  }

  _textCell(text) {
    const td = document.createElement("td")
    td.className = "px-3 py-2"
    td.textContent = text
    return td
  }

  remove(event) {
    const button = event.currentTarget
    const row = button.closest('tr')
    const destroyInput = row.querySelector('input[name*="[_destroy]"]')

    if (destroyInput) {
      destroyInput.value = "1"
      row.classList.add("hidden")
    } else {
      row.remove()
    }

    this.updateDuration()
  }
}
